import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {Badge, Form, OverlayTrigger, Row, Spinner, Tooltip} from 'react-bootstrap';
import {Timeline as GenericTimeline} from '../../../../components/Timeline';
import {formatDateVanilla, formatDateWithTime, formatLocalDate, splitDateTime,} from '../../../../utils/util';
import EventTimelineCard from './EventTimelineCard';
import {useEventTimelineContext} from "../context/EventTimeline.context";
import {FilterGroupNew} from '../../../../components/filters/filterGroupNew';
import {getFilterDef} from '../../../../components/filters/filterDefinition';
import {isEmpty} from "lodash";

const componentName = 'eventTimeline';
const SEARCH_DELAY_MS = 600;
const MIN_SEARCH_LENGTH = 3;

const getTimelineRequestKey = (patientId, requestBody) => {
  const search = requestBody ?? {};
  return `${patientId}:${JSON.stringify(search)}`;
};

const millisecondsRemover = (generatedAtFormatted) => {
  if (!generatedAtFormatted) {
    return null;
  }

  const date = generatedAtFormatted.split(":");
  date.pop();
  return date.join('-');
};

const buildTimelineTitle = (eventTimeline, idx, generatedAtAlreadySeenSet) => {
  // by definition, events with the same date (excluding milliseconds) must be grouped
  const generatedAtWithoutMilliseconds = millisecondsRemover(
    formatDateWithTime(eventTimeline.generatedAt)
  );
  const shouldShowGeneratedAt = !generatedAtAlreadySeenSet.has(generatedAtWithoutMilliseconds);

  if (shouldShowGeneratedAt) {
    generatedAtAlreadySeenSet.add(generatedAtWithoutMilliseconds);
  }

  const [, time] = splitDateTime(formatDateVanilla(eventTimeline?.generatedAt, "MMM dd, yyyy, hh:mm a"));
  const isEncounterOrLabData = ['Encounter', 'Lab Data'].includes(eventTimeline.type);

  if (eventTimeline.generatedAt && shouldShowGeneratedAt) {
    return (
      <div>
        <span id={`${componentName}-tlDate-${idx}`}
          className="d-block fw-bold">
          {isEncounterOrLabData ? formatDateVanilla(eventTimeline?.generatedAt, "MMM dd, yyyy", true) : formatLocalDate(
              eventTimeline.generatedAt)}
        </span>
        <span id={`${componentName}-tlTime-${idx}`} className="d-block">
          {isEncounterOrLabData ? '' : time}
        </span>
      </div>
    );
  }

  if (isEmpty(generatedAtWithoutMilliseconds)) {
    return "-";
  }

  return "";
};

const buildTimelineBody = (eventTimeline, idx, openStates, toggleIndividual) => (
  <EventTimelineCard
    section={'all'}
    idx={idx}
    eventTimeline={eventTimeline}
    toggleIndividual={() => toggleIndividual(idx)}
    open={openStates[idx]}
  />
);

function EventTimeline({ patient, reachedBottom, setReachedBottom }) {
  const {
    getEventsTimeline,
    eventsTimelineList,
    userNameListFilter,
    scrollEventsTimeline,
    isLoading,
    hasMore,
    totalSize,
    eventTimelineSearch,
    availableEventTypes
  } = useEventTimelineContext();

  const [filterParams, setFilterParams] = useState([]);
  const [filters, setFilters] = useState([]);
  const [openStates, setOpenStates] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const searchTimeoutRef = useRef(null);
  const previousSearchLengthRef = useRef(0);
  const initialSearchRef = useRef({});
  const initialLoadKeyRef = useRef(null);
  const fullSearchLoadKeyRef = useRef(null);
  const patientId = patient?.id;
  const eventsTimelineLength = eventsTimelineList?.length ?? 0;

  const normalizeSearchValue = useCallback((value) => {
    if (value === undefined || value === null) return "";
    const normalized = String(value).trim().toLowerCase();
    return normalized.length ? normalized : "";
  }, []);

  const collectSearchValues = useCallback((value, output, seen) => {
    if (value === undefined || value === null) return;
    const type = typeof value;
    if (type === "string" || type === "number" || type === "boolean") {
      const normalized = normalizeSearchValue(value);
      if (normalized) {
        output.push(normalized);
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(item => collectSearchValues(item, output, seen));
      return;
    }
    if (type === "object") {
      if (seen.has(value)) return;
      seen.add(value);
      Object.values(value).forEach(item => collectSearchValues(item, output, seen));
    }
  }, [normalizeSearchValue]);

  const doesEventMatchSearch = useCallback((eventTimeline, term) => {
    const normalizedTerm = normalizeSearchValue(term);
    if (!normalizedTerm) return true;
    const values = [];
    collectSearchValues(eventTimeline, values, new Set());
    return values.some((candidate) => candidate.includes(normalizedTerm));
  }, [collectSearchValues, normalizeSearchValue]);

  const isSearchActive = activeSearchTerm.trim().length >= MIN_SEARCH_LENGTH;
  const filteredEvents = useMemo(() => {
    if (!isSearchActive) return eventsTimelineList ?? [];
    return (eventsTimelineList ?? []).filter((eventTimeline) => doesEventMatchSearch(eventTimeline, activeSearchTerm));
  }, [isSearchActive, eventsTimelineList, doesEventMatchSearch, activeSearchTerm]);

  useEffect(() => {
    if (patientId) {
      const initialSearch = eventTimelineSearch ?? {};
      const requestKey = getTimelineRequestKey(patientId, initialSearch);
      if (initialLoadKeyRef.current === requestKey) {
        return;
      }
      initialLoadKeyRef.current = requestKey;
      fullSearchLoadKeyRef.current = null;
      initialSearchRef.current = initialSearch;
      setSearchTerm("");
      setActiveSearchTerm("");
      previousSearchLengthRef.current = 0;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      getEventsTimeline(patientId, initialSearch);
    }
    // EventTimelineContext updates eventTimelineSearch and callback identities after fetches.
    // The initial drawer load should run only when the selected patient changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  useEffect(() => {
    if (isSearchActive) return;
    if (eventsTimelineLength > 0 && reachedBottom && !isLoading && hasMore) {
      scrollEventsTimeline(patientId, eventTimelineSearch)
        .then(() => setReachedBottom(false))
    }
  }, [reachedBottom, isSearchActive, isLoading, hasMore, eventTimelineSearch, setReachedBottom, scrollEventsTimeline, eventsTimelineLength, patientId])

  useEffect(() => {
    if (filteredEvents) {
      setOpenStates(prevStates => {
        const newStates = new Array(filteredEvents.length).fill(expandAll);
        prevStates.forEach((state, index) => {
          if (index < newStates.length) {
            newStates[index] = state;
          }
        });
        if (
          prevStates.length === newStates.length &&
          prevStates.every((state, index) => state === newStates[index])
        ) {
          return prevStates;
        }
        return newStates;
      });
    }
  }, [expandAll, filteredEvents])

  const generatedAtAlreadySeenSet = new Set();

  useEffect(() => {
    const filterNames = [
      'eventType',
      'Date',
      'User Name',
      'Has Attachment'
    ]

    setFilters(filterNames.map(f => {
      let filterProps = getFilterDef(f);
      const filtersValueByFilter = {'User Name': userNameListFilter, 'eventType': availableEventTypes};
      if (filtersValueByFilter[f]) {
        filterProps = {
          ...filterProps,
          componentProps: {
            ...filterProps.componentProps,
            getData: () => Promise.resolve(filtersValueByFilter[f])
          }
        }
      }
      return filterProps;
    }));
  }, [userNameListFilter, availableEventTypes])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!patientId) return;
    if (!isSearchActive) return;
    if (!totalSize) return;
    if (eventsTimelineLength >= totalSize) return;
    const fullSearchRequest = {
      ...initialSearchRef.current,
      pageNumber: 1,
      pageSize: totalSize,
    };
    const requestKey = getTimelineRequestKey(patientId, fullSearchRequest);
    if (fullSearchLoadKeyRef.current === requestKey) return;
    fullSearchLoadKeyRef.current = requestKey;
    getEventsTimeline(patientId, fullSearchRequest);
    // Loading the full list for client-side search should not restart when context search state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchActive, totalSize, patientId, eventsTimelineLength]);

  const toggleAll = (state) => {
    setExpandAll(state)
    setOpenStates(prev => prev.map(() => state));
  };

  const toggleIndividual = useCallback((index) => {
    setOpenStates(prev => prev.map((state, i) => i === index ? !state : state));
  }, []);

  const handleSearchChange = (value = "") => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const trimmed = value.trim();
    const previousLength = previousSearchLengthRef.current;
    const shouldReset =
      trimmed.length === 0 || (trimmed.length < MIN_SEARCH_LENGTH && previousLength >= MIN_SEARCH_LENGTH);
    if (shouldReset) {
      setActiveSearchTerm("");
    }
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH && previousLength < MIN_SEARCH_LENGTH) {
      previousSearchLengthRef.current = trimmed.length;
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      const currentTrimmed = value.trim();
      const currentLength = currentTrimmed.length;
      const shouldSearch = currentLength >= MIN_SEARCH_LENGTH;
      if (!shouldSearch && !shouldReset) {
        previousSearchLengthRef.current = currentLength;
        return;
      }
      setActiveSearchTerm(shouldSearch ? value : "");
      previousSearchLengthRef.current = currentLength;
    }, SEARCH_DELAY_MS);
  };

  const formatCount = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toLocaleString() : "0";
  };

  const titleBuilder = useCallback(
    (eventTimeline, idx) => buildTimelineTitle(eventTimeline, idx, generatedAtAlreadySeenSet),
    [generatedAtAlreadySeenSet]
  );
  const bodyBuilder = useCallback(
    (eventTimeline, idx) => buildTimelineBody(eventTimeline, idx, openStates, toggleIndividual),
    [openStates, toggleIndividual]
  );

  const displayCount = isSearchActive ? filteredEvents.length : totalSize;
  const countPrefix = isSearchActive && Number.isFinite(Number(totalSize))
    ? `${formatCount(filteredEvents.length)} of ${formatCount(totalSize)}`
    : formatCount(displayCount);

  return (
    <Row className={componentName + "-event-timeline-view"}>
      <div className="stickyscroll bg-light">
        <div className="d-flex justify-content-start align-items-center mb-3">
          <FilterGroupNew
            filters={filters}
            handleChange={params => {
              setReachedBottom(false)
              setFilterParams(params)
              getEventsTimeline(patientId, {...eventTimelineSearch, pageNumber: 1, filters: params })
            }}
            defaultFilterParams={[]}
            filterParams={filterParams}
            isManageVisit={true}
          />
        </div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex justify-content-start align-items-center w-100">
            <Badge pill bg="light"
              id={`${componentName}-patientCount`}
              className="border text-dark">
              {countPrefix}{" "}{"Event"}{Number(displayCount) === 1 ? '' : 's'}
            </Badge>
            <Form.Control
              type="search"
              placeholder="Search Events"
              aria-label="Search events within this timeline"
              id={`${componentName}-patientSearch`}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="d-inline-block ms-2 maxw-320"
            />
          </div>
          <div className="d-flex justify-content-end align-items-center">
            <OverlayTrigger
              placement="auto"
              overlay={
                <Tooltip>
                  Expand / Collapse All
                </Tooltip>
              }
            >
              <Badge pill bg="light"
                className="border text-dark mt-2 font-sm text-decoration-none cursor-p"
                onClick={() => toggleAll(!expandAll)}
              >
                <i className='fas fa-angle-double-up'/>
                <i className='fas fa-angle-double-down'/>
              </Badge>
            </OverlayTrigger>
          </div>
        </div>
      </div>
      <GenericTimeline
        items={filteredEvents}
        titleBuilder={titleBuilder}
        bodyBuilder={bodyBuilder}
      />
      {isLoading && (
        <div className={`${componentName}-loading d-flex w-100 justify-content-center my-3`}>
          <Badge pill bg="light" text="dark" className="border d-flex align-items-center">
            <Spinner className="me-2" as="span" animation="border" size="sm" aria-hidden="true" />
            <output>Loading...</output>
          </Badge>
        </div>
      )}
      {
        !hasMore &&
        <div className="d-flex w-100 justify-content-center">
          <p>You have reached the bottom</p>
        </div>
      }
    </Row>
  )
}

EventTimeline.propTypes = {
  patient: PropTypes.object,
  reachedBottom: PropTypes.bool,
  setReachedBottom: PropTypes.func,
};

export default EventTimeline
